import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMessages } from "@/contexts/MessagesContext";

function timeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function MessagesBell() {
  const { user } = useAuth();
  const { unread, openWith } = useMessages();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dm/threads");
      setThreads(Array.isArray(data) ? data : []);
    } catch { setThreads([]); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadThreads();
  };

  if (!user) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={toggle}
        data-testid="messages-bell"
        className="relative w-9 h-9 flex items-center justify-center rounded-full bg-[var(--tt-surface-2)] hover:bg-[#222] border border-[var(--tt-border)] transition-colors"
        title="Messages"
      >
        <MessageSquare className="w-4 h-4 text-[var(--tt-purple)]" />
        {unread > 0 && (
          <span
            data-testid="messages-unread-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--tt-primary)] text-white text-[10px] font-bold flex items-center justify-center border-2 border-black"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="messages-dropdown"
          className="absolute right-0 mt-2 w-[340px] max-w-[calc(100vw-24px)] bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center justify-between">
            <div className="font-display font-bold text-sm flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5 text-[var(--tt-purple)]" />
              Messages
            </div>
            <button
              onClick={() => { setOpen(false); navigate("/messages"); }}
              data-testid="messages-open-page"
              className="text-[10px] uppercase tracking-wider font-bold text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)] flex items-center gap-1"
            >
              Open Inbox <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-xs text-[var(--tt-text-muted)] py-6">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
              </div>
            ) : threads.length === 0 ? (
              <div className="px-4 py-8 text-xs text-[var(--tt-text-muted)] text-center">
                No conversations yet. Send a message from any user profile.
              </div>
            ) : (
              <ul>
                {threads.map((t) => (
                  <li
                    key={t.thread_id}
                    onClick={() => { setOpen(false); openWith(t.peer); }}
                    data-testid={`thread-${t.thread_id}`}
                    className={`px-4 py-3 cursor-pointer border-b border-[var(--tt-border)] last:border-b-0 hover:bg-white/[0.04] ${t.unread > 0 ? "bg-[var(--tt-primary)]/[0.05]" : ""}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {t.peer.picture ? (
                        <img src={t.peer.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-xs font-bold shrink-0">
                          {(t.peer.name || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-sm truncate">{t.peer.name}</span>
                          <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt ml-auto">{timeShort(t.last_message.created_at)}</span>
                        </div>
                        <div className="text-xs text-[var(--tt-text-muted)] truncate mt-0.5">
                          {t.last_message.from_me ? "You: " : ""}{t.last_message.text}
                        </div>
                      </div>
                      {t.unread > 0 && (
                        <span className="ml-1 mt-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--tt-primary)] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                          {t.unread}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
